import { Button } from '@/components/ui/button';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    Activity,
    Award,
    Boxes,
    Building2,
    CheckCircle,
    ChevronRight,
    ClipboardList,
    Database,
    DollarSign,
    FileText,
    Globe,
    Layers,
    MapPin,
    MapPinned,
    Package,
    PackageCheck,
    Plus,
    QrCode,
    Search,
    Settings,
    Shield,
    Tags,
    Truck,
    Users,
} from 'lucide-react';
import React, { useMemo, useState } from 'react';

import { useAuth } from '../hooks/useAuth';
import { useMasterDataCounts } from '../hooks/useMasterDataCounts';
import AwbStateManagement from './AwbStateManagement';
import AwbStatusManagement from './AwbStatusManagement';
import CityManagement from './CityManagement';
import CompanyManagement from './CompanyManagement';
import CountryManagement from './CountryManagement';
import CurrencyManagement from './CurrencyManagement';
import CustomerManagement from './CustomerManagement';
import DispatchManagement from './DispatchManagement';
import DistrictManagement from './DistrictManagement';
import EdiBagNoManagement from './EdiBagNoManagement';
import FreeFormItemManagement from './FreeFormItemManagement';
import FreeFormNoteManagement from './FreeFormNoteManagement';
import HubManagement from './HubManagement';
import IncotermManagement from './IncotermManagement';
import KYCManagement from './KYCManagement';
import LicenseLocationManagement from './LicenseLocationManagement';
import LicenseManagement from './LicenseManagement';
import PincodeManagement from './PincodeManagement';
import ProductFormatManagement from './ProductFormatManagement';
import ProductManagement from './ProductManagement';
import ServiceManagement from './ServiceManagement';
import ServiceTypeManagement from './ServiceTypeManagement';
import StateManagement from './StateManagement';
import VendorManagement from './VendorManagement';
import VendorTypeManagement from './VendorTypeManagement';
import ZoneManagement from './ZoneManagement';

interface MasterDataEntity {
  id: string;
  name: string;
  description: string;
  icon: React.ComponentType<any>;
  component: React.ComponentType<any>;
  count?: number;
  apiEndpoint: string;
  category: string;
}

// ERP-style category configuration
const categories = [
  { id: 'core', name: 'Core Business', icon: Building2, color: 'blue' },
  { id: 'logistics', name: 'Logistics', icon: Truck, color: 'green' },
  { id: 'geographic', name: 'Geographic', icon: Globe, color: 'purple' },
  { id: 'config', name: 'Configuration', icon: Settings, color: 'orange' },
  { id: 'finance', name: 'Finance', icon: DollarSign, color: 'emerald' },
  { id: 'operations', name: 'Operations', icon: PackageCheck, color: 'rose' },
];

const MasterDataManagement = () => {
  const [activeTab, setActiveTab] = useState('customers');
  const [activeCategory, setActiveCategory] = useState('core');
  const [searchQuery, setSearchQuery] = useState('');
  const { axios } = useAuth();

  // Optimized data loading with custom hook
  const entityEndpoints = [
    'customer',
    'company',
    'hub',
    'product',
    'productFormat',
    'vendor',
    'vendorType',
    'dispatch',
    'freeFormItem',
    'freeFormNote',
    'incoterm',
    'awbState',
    'awbStatus',
    'license',
    'licenseLocation',
    'country',
    'currency',
    'service',
    'serviceType',
    'kyc',
    'state',
    'zone',
    'district',
    'ediBagNo',
    'pincode',
    'city',
  ];
  const { counts, loading } = useMasterDataCounts(entityEndpoints);

  // Map loading states for entities
  const entityLoadingStates = {
    customers: loading.customer,
    companies: loading.company,
    hubs: loading.hub,
    products: loading.product,
    productFormats: loading.productFormat,
    vendors: loading.vendor,
    vendorTypes: loading.vendorType,
    dispatches: loading.dispatch,
    freeFormItems: loading.freeFormItem,
    freeFormNotes: loading.freeFormNote,
    incoterms: loading.incoterm,
    awbStates: loading.awbState,
    awbStatuses: loading.awbStatus,
    licenses: loading.license,
    licenseLocations: loading.licenseLocation,
    countries: loading.country,
    currencies: loading.currency,
    services: loading.service,
    serviceTypes: loading.serviceType,
    kyc: loading.kyc,
    states: loading.state,
    zones: loading.zone,
    districts: loading.district,
    ediBagNos: loading.ediBagNo,
    pincodes: loading.pincode,
    cities: loading.city,
  };

  const entities: MasterDataEntity[] = [
    // Core Business
    {
      id: 'customers',
      name: 'Customers',
      description: 'Customer profiles and contact information',
      icon: Users,
      component: CustomerManagement,
      count: counts.customer || 0,
      apiEndpoint: 'customer',
      category: 'core',
    },
    {
      id: 'companies',
      name: 'Companies',
      description: 'Organization details and business settings',
      icon: Building2,
      component: CompanyManagement,
      count: counts.company || 0,
      apiEndpoint: 'company',
      category: 'core',
    },
    {
      id: 'vendors',
      name: 'Vendors',
      description: 'Partner vendors and supplier information',
      icon: Truck,
      component: VendorManagement,
      count: counts.vendor || 0,
      apiEndpoint: 'vendor',
      category: 'core',
    },
    // Logistics
    {
      id: 'hubs',
      name: 'Hubs',
      description: 'Distribution centers and logistics hubs',
      icon: MapPin,
      component: HubManagement,
      count: counts.hub || 0,
      apiEndpoint: 'hub',
      category: 'logistics',
    },
    {
      id: 'products',
      name: 'Products',
      description: 'Product catalog and specifications',
      icon: Package,
      component: ProductManagement,
      count: counts.product || 0,
      apiEndpoint: 'product',
      category: 'logistics',
    },
    {
      id: 'productFormats',
      name: 'Product Formats',
      description: 'Product format types (Package, Dox, Non Dox, etc.)',
      icon: Layers,
      component: ProductFormatManagement,
      count: counts.productFormat || 0,
      apiEndpoint: 'productFormat',
      category: 'logistics',
    },
    {
      id: 'services',
      name: 'Services',
      description: 'Service types and delivery options',
      icon: Settings,
      component: ServiceManagement,
      count: counts.service || 0,
      apiEndpoint: 'service',
      category: 'logistics',
    },
    {
      id: 'serviceTypes',
      name: 'Service Types',
      description: 'Master data for service type definitions',
      icon: PackageCheck,
      component: ServiceTypeManagement,
      count: counts.serviceType || 0,
      apiEndpoint: 'serviceType',
      category: 'logistics',
    },
    // Geographic
    {
      id: 'countries',
      name: 'Countries',
      description: 'Geographic regions and shipping zones',
      icon: Globe,
      component: CountryManagement,
      count: counts.country || 0,
      apiEndpoint: 'country',
      category: 'geographic',
    },
    {
      id: 'states',
      name: 'States',
      description: 'State management with port codes and GST',
      icon: MapPin,
      component: StateManagement,
      count: counts.state || 0,
      apiEndpoint: 'state',
      category: 'geographic',
    },
    {
      id: 'districts',
      name: 'Districts',
      description: 'District management within states',
      icon: MapPin,
      component: DistrictManagement,
      count: counts.district || 0,
      apiEndpoint: 'district',
      category: 'geographic',
    },
    {
      id: 'zones',
      name: 'Zones',
      description: 'Geographic zones and area management',
      icon: MapPin,
      component: ZoneManagement,
      count: counts.zone || 0,
      apiEndpoint: 'zone',
      category: 'geographic',
    },
    {
      id: 'cities',
      name: 'Cities',
      description: 'City management within districts',
      icon: MapPin,
      component: CityManagement,
      count: counts.city || 0,
      apiEndpoint: 'city',
      category: 'geographic',
    },
    {
      id: 'pincodes',
      name: 'Pincodes',
      description: 'Pincode management with pickup availability',
      icon: MapPinned,
      component: PincodeManagement,
      count: counts.pincode || 0,
      apiEndpoint: 'pincode',
      category: 'geographic',
    },
    // Configuration
    {
      id: 'awbStates',
      name: 'AWB States',
      description: 'AWB state configurations for tracking',
      icon: Activity,
      component: AwbStateManagement,
      count: counts.awbState || 0,
      apiEndpoint: 'awbState',
      category: 'config',
    },
    {
      id: 'awbStatuses',
      name: 'AWB Statuses',
      description: 'AWB status and invoice configurations',
      icon: CheckCircle,
      component: AwbStatusManagement,
      count: counts.awbStatus || 0,
      apiEndpoint: 'awbStatus',
      category: 'config',
    },
    {
      id: 'licenses',
      name: 'Licenses',
      description: 'License and courier registration management',
      icon: Award,
      component: LicenseManagement,
      count: counts.license || 0,
      apiEndpoint: 'license',
      category: 'config',
    },
    {
      id: 'licenseLocations',
      name: 'License Locations',
      description: 'Locations linked to licenses',
      icon: MapPinned,
      component: LicenseLocationManagement,
      count: counts.licenseLocation || 0,
      apiEndpoint: 'licenseLocation',
      category: 'config',
    },
    // Finance
    {
      id: 'currencies',
      name: 'Currencies',
      description: 'Supported currencies and exchange rates',
      icon: DollarSign,
      component: CurrencyManagement,
      count: counts.currency || 0,
      apiEndpoint: 'currency',
      category: 'finance',
    },
    {
      id: 'incoterms',
      name: 'Incoterms',
      description: 'International commercial terms for trade',
      icon: FileText,
      component: IncotermManagement,
      count: counts.incoterm || 0,
      apiEndpoint: 'incoterm',
      category: 'finance',
    },
    {
      id: 'kyc',
      name: 'KYC',
      description: 'Know Your Customer types and compliance',
      icon: Shield,
      component: KYCManagement,
      count: counts.kyc || 0,
      apiEndpoint: 'kyc',
      category: 'finance',
    },
    // Operations
    {
      id: 'dispatches',
      name: 'Dispatch Types',
      description: 'Dispatch types and delivery methods',
      icon: PackageCheck,
      component: DispatchManagement,
      count: counts.dispatch || 0,
      apiEndpoint: 'dispatch',
      category: 'operations',
    },
    {
      id: 'vendorTypes',
      name: 'Vendor Types',
      description: 'Master data for vendor type definitions',
      icon: Tags,
      component: VendorTypeManagement,
      count: counts.vendorType || 0,
      apiEndpoint: 'vendorType',
      category: 'operations',
    },
    {
      id: 'freeFormItems',
      name: 'Free Form Items',
      description: 'Items with HS codes for customs',
      icon: Boxes,
      component: FreeFormItemManagement,
      count: counts.freeFormItem || 0,
      apiEndpoint: 'freeFormItem',
      category: 'operations',
    },
    {
      id: 'freeFormNotes',
      name: 'Free Form Notes',
      description: 'Notes with shipment values',
      icon: ClipboardList,
      component: FreeFormNoteManagement,
      count: counts.freeFormNote || 0,
      apiEndpoint: 'freeFormNote',
      category: 'operations',
    },
    {
      id: 'ediBagNos',
      name: 'EDI Bag Numbers',
      description: 'EDI bag number ranges and configs',
      icon: QrCode,
      component: EdiBagNoManagement,
      count: counts.ediBagNo || 0,
      apiEndpoint: 'ediBagNo',
      category: 'operations',
    },
  ];

  // Filter entities by category and search
  const filteredEntities = useMemo(() => {
    return entities.filter(entity => {
      const matchesCategory = activeCategory === 'all' || entity.category === activeCategory;
      const matchesSearch = searchQuery === '' || 
        entity.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        entity.description.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [activeCategory, searchQuery, entities]);

  // Get entities for all category (search result)
  const allEntities = useMemo(() => {
    if (searchQuery === '') return [];
    return entities.filter(entity => 
      entity.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      entity.description.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [searchQuery, entities]);

  // Calculate category stats
  const categoryStats = useMemo(() => {
    return categories.map(cat => ({
      ...cat,
      entityCount: entities.filter(e => e.category === cat.id).length,
      totalRecords: entities.filter(e => e.category === cat.id).reduce((sum, e) => sum + (e.count || 0), 0),
    }));
  }, [entities]);

  const activeEntity = entities.find(e => e.id === activeTab);
  const ActiveComponent = activeEntity?.component;

  // Get category color classes
  const getCategoryColor = (categoryId: string) => {
    const colorMap: Record<string, { bg: string; text: string; border: string }> = {
      core: { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200' },
      logistics: { bg: 'bg-green-50', text: 'text-green-600', border: 'border-green-200' },
      geographic: { bg: 'bg-purple-50', text: 'text-purple-600', border: 'border-purple-200' },
      config: { bg: 'bg-orange-50', text: 'text-orange-600', border: 'border-orange-200' },
      finance: { bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-200' },
      operations: { bg: 'bg-rose-50', text: 'text-rose-600', border: 'border-rose-200' },
    };
    return colorMap[categoryId] || colorMap.core;
  };

  const handleEntitySelect = (entityId: string) => {
    setActiveTab(entityId);
  };

  return (
    <div className='min-h-screen bg-background'>
      <div className='container mx-auto px-4 sm:px-6 py-4 sm:py-6 max-w-[1600px]'>
        <div className='space-y-4'>
          {/* Header with Search - ERP Style */}
          <div className='flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 pb-4 border-b'>
            <div className='flex items-center gap-3'>
              <div className='p-2 bg-primary/10 rounded-lg'>
                <Database className='h-6 w-6 text-primary' />
              </div>
              <div>
                <h1 className='text-xl sm:text-2xl font-bold tracking-tight'>
                  Master Data Management
                </h1>
                <p className='text-sm text-muted-foreground'>
                  Centralized ERP data configuration
                </p>
              </div>
            </div>

            {/* Global Search */}
            <div className='flex items-center gap-3'>
              <div className='relative w-full lg:w-80'>
                <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
                <Input
                  placeholder='Search master data...'
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className='pl-9 h-10'
                />
              </div>
              
              {/* Quick Stats */}
              <div className='hidden md:flex items-center gap-2 text-sm'>
                <span className='px-3 py-1.5 bg-primary/10 text-primary rounded-full font-medium'>
                  {entities.reduce((sum, e) => sum + (e.count || 0), 0)} Records
                </span>
                <span className='px-3 py-1.5 bg-green-100 text-green-700 rounded-full font-medium'>
                  {entities.filter(e => (e.count || 0) > 0).length}/{entities.length} Active
                </span>
              </div>
            </div>
          </div>

          {/* Category Tabs - ERP Style */}
          <Tabs value={activeCategory} onValueChange={setActiveCategory} className='w-full'>
            <div className='overflow-x-auto'>
              <TabsList className='inline-flex h-12 p-1 bg-muted/50 rounded-lg min-w-max'>
                {categoryStats.map(cat => {
                  const Icon = cat.icon;
                  const colors = getCategoryColor(cat.id);
                  return (
                    <TabsTrigger
                      key={cat.id}
                      value={cat.id}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-md transition-all data-[state=active]:bg-background data-[state=active]:shadow-sm`}
                    >
                      <Icon className='h-4 w-4' />
                      <span className='hidden sm:inline font-medium'>{cat.name}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${colors.bg} ${colors.text}`}>
                        {cat.totalRecords}
                      </span>
                    </TabsTrigger>
                  );
                })}
              </TabsList>
            </div>

            {/* Entity List & Content Area */}
            <div className='mt-4 grid grid-cols-1 xl:grid-cols-12 gap-4'>
              {/* Entity List Sidebar */}
              <div className='xl:col-span-3'>
                <Card className='border shadow-sm'>
                  <CardHeader className='py-3 px-4 border-b bg-muted/30'>
                    <div className='flex items-center justify-between'>
                      <CardTitle className='text-sm font-semibold'>
                        {searchQuery ? 'Search Results' : categories.find(c => c.id === activeCategory)?.name || 'Entities'}
                      </CardTitle>
                      <span className='text-xs text-muted-foreground'>
                        {searchQuery ? allEntities.length : filteredEntities.length} items
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent className='p-0 max-h-[400px] overflow-y-auto'>
                    <div className='divide-y'>
                      {(searchQuery ? allEntities : filteredEntities).map(entity => {
                        const Icon = entity.icon;
                        const colors = getCategoryColor(entity.category);
                        const isActive = activeTab === entity.id;
                        const isLoading = entityLoadingStates[entity.id as keyof typeof entityLoadingStates];
                        
                        return (
                          <button
                            key={entity.id}
                            onClick={() => handleEntitySelect(entity.id)}
                            className={`w-full flex items-center gap-3 p-3 text-left transition-all hover:bg-muted/50 ${
                              isActive ? 'bg-primary/5 border-l-2 border-l-primary' : ''
                            }`}
                          >
                            <div className={`p-2 rounded-lg ${colors.bg}`}>
                              <Icon className={`h-4 w-4 ${colors.text}`} />
                            </div>
                            <div className='flex-1 min-w-0'>
                              <div className='flex items-center justify-between'>
                                <span className={`text-sm font-medium truncate ${isActive ? 'text-primary' : ''}`}>
                                  {entity.name}
                                </span>
                                <div className='flex items-center gap-2'>
                                  <span className='text-xs text-muted-foreground tabular-nums'>
                                    {isLoading ? '...' : entity.count || 0}
                                  </span>
                                  {(entity.count || 0) > 0 && (
                                    <span className='w-2 h-2 rounded-full bg-green-500' />
                                  )}
                                </div>
                              </div>
                              <p className='text-xs text-muted-foreground truncate'>
                                {entity.description}
                              </p>
                            </div>
                            <ChevronRight className={`h-4 w-4 text-muted-foreground transition-transform ${isActive ? 'text-primary rotate-90' : ''}`} />
                          </button>
                        );
                      })}
                      
                      {(searchQuery ? allEntities : filteredEntities).length === 0 && (
                        <div className='p-8 text-center text-muted-foreground'>
                          <Search className='h-8 w-8 mx-auto mb-2 opacity-50' />
                          <p className='text-sm'>No entities found</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Quick Add Button */}
                <Button 
                  className='w-full mt-3 gap-2' 
                  variant='outline'
                  onClick={() => {
                    // Trigger add action on active entity
                    const addButton = document.querySelector('[data-testid="add-button"]') as HTMLButtonElement;
                    if (addButton) addButton.click();
                  }}
                >
                  <Plus className='h-4 w-4' />
                  Add New {activeEntity?.name.replace(/s$/, '') || 'Record'}
                </Button>
              </div>

              {/* Main Content Area */}
              <div className='xl:col-span-9'>
                <Card className='border shadow-sm'>
                  {/* Breadcrumb Header */}
                  <CardHeader className='py-3 px-4 border-b bg-muted/30'>
                    <div className='flex items-center justify-between'>
                      <div className='flex items-center gap-2 text-sm'>
                        <span className='text-muted-foreground'>Master Data</span>
                        <ChevronRight className='h-4 w-4 text-muted-foreground' />
                        <span className='text-muted-foreground'>
                          {categories.find(c => c.id === activeEntity?.category)?.name}
                        </span>
                        <ChevronRight className='h-4 w-4 text-muted-foreground' />
                        <span className='font-semibold text-foreground flex items-center gap-2'>
                          {activeEntity?.name}
                          <span className='px-2 py-0.5 text-xs bg-primary/10 text-primary rounded-full'>
                            {activeEntity?.count || 0} records
                          </span>
                        </span>
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent className='p-4'>
                    {ActiveComponent && <ActiveComponent />}
                  </CardContent>
                </Card>
              </div>
            </div>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default MasterDataManagement;
